// ThemeMotif.tsx — abstract, presentational motifs for the four Library themes.
// One distinct organic form per theme, each with its own "temperature", seeded
// ONLY from that theme's existing base accent (src/services/library.ts →
// CATEGORY_TOKENS). No imagery, no human figures, nothing representational —
// just gradient washes and organic line/blob forms. Inline SVG, static (no
// animation), negligible bundle weight.
//
// Dark mode: the app is light-only (deferred past launch), so there is no dark
// variant yet. If dark mode ships, add a `dark` branch here keyed off the same
// CATEGORY_TOKENS so motifs don't blow out — the call sites won't need changing.
import React from 'react';
import { categoryTokens, PILLARS } from '@/services/library';

const KNOWN = new Set<string>(PILLARS as readonly string[]);

// Render the organic forms for one theme. Coordinates are in a 160×110 viewBox;
// the parent <svg> slices to fill, with forms weighted to the right so the
// title (top-left) always reads over clear space.
function forms(theme: string, base: string): React.ReactNode {
  switch (theme) {
    // Calming Trauma — cool settling water: smooth stacked ripples.
    case 'Calm':
      return (
        <g fill="none" stroke={base} strokeWidth={2.4} strokeLinecap="round">
          <path d="M-10 48 Q 25 36 60 48 T 130 48 T 200 48" opacity={0.9} />
          <path d="M-10 66 Q 25 54 60 66 T 130 66 T 200 66" opacity={0.7} />
          <path d="M-10 84 Q 25 72 60 84 T 130 84 T 200 84" opacity={0.5} />
        </g>
      );
    // Boundaries — a calm protective embrace: nested arcs holding from the edge.
    case 'Hold':
      return (
        <g fill="none" stroke={base} strokeWidth={2.4}>
          <circle cx={158} cy={55} r={26} opacity={0.85} />
          <circle cx={158} cy={55} r={44} opacity={0.6} />
          <circle cx={158} cy={55} r={62} opacity={0.4} />
        </g>
      );
    // Co-Parenting & Conflict — two organic forms meeting and overlapping.
    case 'Respond':
      return (
        <g stroke="none">
          <path
            d="M40 80 C 18 80 14 48 40 42 C 66 36 78 64 60 76 C 52 81 48 80 40 80 Z"
            fill={base}
            opacity={0.5}
          />
          <path
            d="M104 78 C 80 80 76 46 104 40 C 132 34 144 64 124 76 C 116 81 112 78 104 78 Z"
            fill={base}
            opacity={0.32}
          />
        </g>
      );
    // Rebuilding Life — warm upward growth: rising layered ground + sprout lines.
    case 'Rebuild':
      return (
        <g>
          <g fill="none" stroke={base} strokeWidth={2.4} strokeLinecap="round">
            <path d="M-10 104 Q 80 70 200 104" opacity={0.85} />
            <path d="M-10 116 Q 80 84 200 116" opacity={0.55} />
            <path d="M118 92 Q 124 70 138 60" opacity={0.7} />
            <path d="M138 96 Q 140 78 148 66" opacity={0.5} />
          </g>
        </g>
      );
    default:
      return null;
  }
}

export function ThemeMotif({
  theme,
  opacity = 0.16,
  style,
}: {
  theme: string;
  opacity?: number;
  style?: React.CSSProperties;
}) {
  // Orphan keys (Court / Work / Health / …) map to no theme → no motif; the
  // caller falls back to the Phase 1 tint only.
  if (!KNOWN.has(theme)) return null;
  const base = categoryTokens(theme).base;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 160 110"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity,
        pointerEvents: 'none',
        ...style,
      }}
    >
      {forms(theme, base)}
    </svg>
  );
}
